import type { JSX } from 'react';

import type {
  Gewerk,
  LphGroup,
  WorkPackage,
  WorkPackageDuplikat,
  WorkPackageFachgruppeOption,
  WorkPackageLibraryEntry,
} from '@asas/domain';

import { duplikatHinweis, phasenAnzeige, planStundenAnzeige } from './arbeitspakete';

/**
 * Reine Ansicht der Arbeitspakete eines Projekts (AC-303): lädt nichts und ruft
 * nichts auf. Laden und Schreiben stehen in `ArbeitspaketeBereich`.
 */

export interface FreiFormular {
  readonly name: string;
  readonly gewerkId: string;
  /** Index in `lphGroups`; leer heißt „nicht gewählt“. */
  readonly gruppe: string;
  readonly planHours: string;
}

export const LEERES_FREI_FORMULAR: FreiFormular = {
  name: '',
  gewerkId: '',
  gruppe: '',
  planHours: '',
};

export interface ArbeitspaketeAnsichtProps {
  readonly liste: readonly WorkPackage[];
  readonly bibliothek: readonly WorkPackageLibraryEntry[];
  readonly gewerke: readonly Gewerk[];
  readonly fachgruppen: readonly WorkPackageFachgruppeOption[];
  readonly lphGroups: readonly LphGroup[];
  /** Anlegen (Bibliothek und frei) anbieten — `darfArbeitspaketeAnlegen`. */
  readonly darfAnlegen: boolean;
  /** Plan-Stunden ändern anbieten — `projects:manage`. */
  readonly darfPlanStunden: boolean;
  /** Warum nicht angelegt werden kann, wenn das Recht da ist, der Status aber nicht passt. */
  readonly statusHinweis: string | null;
  readonly gesperrt: boolean;
  readonly auswahl: ReadonlySet<string>;
  readonly fachgruppeId: string;
  readonly duplikate: readonly WorkPackageDuplikat[] | null;
  readonly bestaetigt: ReadonlySet<string>;
  readonly frei: FreiFormular;
  readonly planEingaben: Readonly<Record<string, string>>;
  readonly onAuswahl: (libraryId: string, gewaehlt: boolean) => void;
  readonly onFachgruppe: (fachgruppeId: string) => void;
  readonly onBestaetigung: (libraryId: string, bestaetigt: boolean) => void;
  readonly onUebernehmen: () => void;
  readonly onFrei: (frei: FreiFormular) => void;
  readonly onFreiAnlegen: () => void;
  readonly onPlanEingabe: (id: string, wert: string) => void;
  readonly onPlanSpeichern: (arbeitspaket: WorkPackage) => void;
  /**
   * „Ressourcen anfragen" je Arbeitspaket anbieten (AC-404) — nur mit
   * `resource_allocation:request` und aktiver Ressourcenplanung.
   */
  readonly darfAnfragen?: boolean;
  readonly onAnfragen?: (arbeitspaket: WorkPackage) => void;
  /** Während eine Anfrage gesendet wird, kein Wechsel zu einem anderen Arbeitspaket. */
  readonly anfragenGesperrt?: boolean;
}

function gewerkCode(gewerke: readonly Gewerk[], id: string | null): string {
  return id === null ? '–' : (gewerke.find((g) => g.id === id)?.code ?? '–');
}

export function ArbeitspaketeAnsicht(props: ArbeitspaketeAnsichtProps): JSX.Element {
  const { liste, bibliothek, gewerke, fachgruppen, lphGroups, gesperrt } = props;

  return (
    <div>
      {liste.length === 0 ? (
        <p>Noch keine Arbeitspakete in diesem Projekt.</p>
      ) : (
        <table aria-label="Arbeitspakete des Projekts">
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Gewerk</th>
              <th>Leistungsphasen</th>
              <th>Fachgruppe</th>
              <th>Herkunft</th>
              <th>Plan-Stunden</th>
              {props.darfAnfragen === true && <th>Ressourcen</th>}
            </tr>
          </thead>
          <tbody>
            {liste.map((ap) => (
              <tr key={ap.id}>
                <td>{ap.name}</td>
                <td>{gewerkCode(gewerke, ap.gewerkId)}</td>
                <td>{phasenAnzeige(ap.leistungsphasen)}</td>
                <td>{fachgruppen.find((f) => f.id === ap.fachgruppeId)?.name ?? '–'}</td>
                <td>
                  {ap.libraryId === null
                    ? 'freihändig'
                    : `Bibliothek (Version ${String(ap.libraryVersion ?? 0)})`}
                </td>
                <td>
                  {props.darfPlanStunden ? (
                    <>
                      <label>
                        <span className="sr-only">Plan-Stunden {ap.name}</span>
                        <input
                          inputMode="decimal"
                          disabled={gesperrt}
                          value={
                            props.planEingaben[ap.id] ?? (ap.planHours ?? '').replace('.', ',')
                          }
                          onChange={(e) => {
                            props.onPlanEingabe(ap.id, e.target.value);
                          }}
                        />
                      </label>{' '}
                      <button
                        disabled={gesperrt || props.planEingaben[ap.id] === undefined}
                        onClick={() => {
                          props.onPlanSpeichern(ap);
                        }}
                      >
                        Speichern
                      </button>
                    </>
                  ) : (
                    planStundenAnzeige(ap.planHours)
                  )}
                </td>
                {props.darfAnfragen === true && (
                  <td>
                    <button
                      disabled={gesperrt || props.anfragenGesperrt === true}
                      onClick={() => {
                        props.onAnfragen?.(ap);
                      }}
                    >
                      Ressourcen anfragen
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.statusHinweis !== null && <p>{props.statusHinweis}</p>}

      {props.darfAnlegen && (
        <>
          <fieldset>
            <legend>Aus der Bibliothek übernehmen</legend>
            {bibliothek.length === 0 ? (
              <p>Die Bibliothek enthält keine aktiven Stammarbeitspakete.</p>
            ) : (
              <ul>
                {bibliothek.map((eintrag) => (
                  <li key={eintrag.id}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={gesperrt}
                        checked={props.auswahl.has(eintrag.id)}
                        onChange={(e) => {
                          props.onAuswahl(eintrag.id, e.target.checked);
                        }}
                      />{' '}
                      {gewerkCode(gewerke, eintrag.gewerkId)} · {eintrag.name} ·{' '}
                      {phasenAnzeige(eintrag.leistungsphasen)}
                      {eintrag.richtwertH !== null &&
                        ` · Richtwert ${eintrag.richtwertH.replace('.', ',')} h`}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <label>
              Fachgruppe für die Auswahl{' '}
              <select
                disabled={gesperrt}
                value={props.fachgruppeId}
                onChange={(e) => {
                  props.onFachgruppe(e.target.value);
                }}
              >
                <option value="">bitte wählen</option>
                {fachgruppen.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            {props.duplikate !== null && (
              <div>
                <p>{duplikatHinweis(props.duplikate)}</p>
                {props.duplikate.map((d) => (
                  <label key={d.libraryId}>
                    <input
                      type="checkbox"
                      disabled={gesperrt}
                      checked={props.bestaetigt.has(d.libraryId)}
                      onChange={(e) => {
                        props.onBestaetigung(d.libraryId, e.target.checked);
                      }}
                    />{' '}
                    Weiteres Exemplar von „{d.name}“ bewusst anlegen
                  </label>
                ))}
              </div>
            )}
            <button
              disabled={gesperrt || props.auswahl.size === 0 || props.fachgruppeId === ''}
              onClick={props.onUebernehmen}
            >
              {props.auswahl.size === 0 ? 'Übernehmen' : `${String(props.auswahl.size)} übernehmen`}
            </button>
          </fieldset>

          <fieldset>
            <legend>Freihändig anlegen (ohne Bibliothekseintrag)</legend>
            <label>
              Bezeichnung{' '}
              <input
                disabled={gesperrt}
                value={props.frei.name}
                onChange={(e) => {
                  props.onFrei({ ...props.frei, name: e.target.value });
                }}
              />
            </label>
            <label>
              Gewerk{' '}
              <select
                disabled={gesperrt}
                value={props.frei.gewerkId}
                onChange={(e) => {
                  props.onFrei({ ...props.frei, gewerkId: e.target.value });
                }}
              >
                <option value="">bitte wählen</option>
                {gewerke.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.code} – {g.bezeichnung}
                  </option>
                ))}
              </select>
            </label>
            <label>
              LPH-Gruppe{' '}
              <select
                disabled={gesperrt}
                value={props.frei.gruppe}
                onChange={(e) => {
                  props.onFrei({ ...props.frei, gruppe: e.target.value });
                }}
              >
                <option value="">bitte wählen</option>
                {lphGroups.map((g, index) => (
                  <option key={g.label} value={String(index)}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Plan-Stunden (optional){' '}
              <input
                inputMode="decimal"
                disabled={gesperrt}
                value={props.frei.planHours}
                onChange={(e) => {
                  props.onFrei({ ...props.frei, planHours: e.target.value });
                }}
              />
            </label>
            <button
              disabled={
                gesperrt ||
                props.frei.name.trim() === '' ||
                props.frei.gewerkId === '' ||
                props.frei.gruppe === '' ||
                props.fachgruppeId === ''
              }
              onClick={props.onFreiAnlegen}
            >
              Freihändig anlegen
            </button>
            <p>Die Fachgruppe kommt aus der Auswahl oben.</p>
          </fieldset>
        </>
      )}
    </div>
  );
}
